// resolve.ts — configuration in, a total CurrencyPresentation out.
//
// Every currency always renders. Configuration is allowed to be missing,
// partial, empty-stringed or nonsense; none of those may reach the screen as a
// blank label, a NaN precision or a broken image. So each field is validated
// independently and falls back to a frozen seed on its own — a currency with a
// good name but a junk colour keeps the name.
//
// "Deterministic seeded fallbacks" (SM-443) means exactly what it says: the
// seed table below is a constant. An unconfigured RED looks identical in Web
// Client, Backoffice and MM Web, in every session, in every test run. Nothing
// here reads the clock, the locale, a random source or the network.

import coinGoldUrl from "./assets/coin-gold.svg";
import coinGreenUrl from "./assets/coin-green.svg";
import coinRedUrl from "./assets/coin-red.svg";
import {
  CURRENCY_CODES,
  UnknownCurrencyError,
  isCurrencyCode,
  type CurrencyCode,
  type CurrencyIconSpec,
  type CurrencyPresentation,
  type CurrencyPresentationSettings,
} from "./types";

/** Display decimals are clamped here. 4 is the wire's minor-unit precision — a
 *  display cannot be more precise than the value it is showing. */
export const MAX_DISPLAY_DECIMALS = 4;

/**
 * The one accepted display divisor. SM-484 allowed two — 100 for GOLD's legacy
 * SPARK lens and 10 000 for the $1 unit — and that split was the defect: it let
 * the same wire integer render "$0.50" as a price and "⚡50.00" as an amount.
 * SM-460 says one displayed whole unit of ANY of the three currencies is $1, so
 * there is exactly one divisor and a second value is not a configuration
 * choice, it is a currency being silently re-based. Anything else falls back to
 * the seed — never to 0, which is an Infinity on screen.
 */
const VALID_UNIT_MINORS: readonly number[] = Object.freeze([10_000]);

/**
 * The seed. Frozen, exhaustive, and the single answer to "what does an
 * unconfigured currency look like".
 *
 * Three deliberate choices:
 *
 *  - All three currencies seed the platform's own coin artwork, GOLD
 *    included. GOLD used to seed a monogram here, for a reason that has
 *    since been retired: SM-334/SM-441 made a currency's mark the BRAND's
 *    coin, so the player app resolved GOLD to whichever coin its host was
 *    serving — something this console, operated across brands, could not
 *    depict without picking one and lying about the others.
 *
 *    SM-488 removed that per-brand fork. One account holds one GOLD wallet
 *    described by one registry row, so GOLD is a platform currency exactly
 *    as RED and GREEN are, and it now ships the same coin SVG the player
 *    apps ship. This console showing an operator the mark players actually
 *    see is the whole point of copying the artwork rather than inventing
 *    a console-only one. An upload on /currencies still overrides it, and
 *    a monogram is now only the degrade path for artwork that fails to load.
 *
 *    SM-505 replaced all three coins with the artwork the product supplied:
 *    a ringed coin carrying a bar chart for RED and GREEN, a crown for GOLD,
 *    in the supplied palettes. Only what the seed LOOKS like changed. It is
 *    still reached exactly when a currency has no configured `image_url`, so
 *    an operator's uploaded mark still wins and this overwrites nothing. The
 *    supplied files are 8334x8334 PNGs; the seed is authored as vector so it
 *    stays crisp at the 1em a mark actually renders at, and so MM Web can
 *    hold the same bytes inline.
 *
 *    The RED and GREEN coins were then SWAPPED. The supplied files map by
 *    filename — `default-currency-red.png` for RED — but that file is green
 *    artwork and `default-currency-green.png` is teal, so seeding them as
 *    named put a green coin on the currency whose own colour is #ef4444.
 *    GREEN now draws the green coin and RED the teal one, on the product's
 *    instruction (SM-505). Nothing but which file each code points at
 *    changed: the bytes are the supplied bytes, and the digest table in the
 *    parity suites moved with them.
 *
 *  - No currency seeds "$" as its symbol. This is an OPERATOR console:
 *    a money figure here must name which platform currency it is, and a
 *    dollar sign claims USD-ness the ledger never promised (one whole
 *    unit REPRESENTS $1 — a display convention, not a peg). GOLD seeds
 *    its code like the other two; the 0–1 PRICE grid is unitless ("0.50"
 *    through formatPrice, SM-986) and is unaffected by the symbol.
 *    RED and GREEN keep their code as the symbol: they are platform
 *    currencies with no dollar sign of their own, and a code never collides,
 *    reads correctly in every locale, and is visibly provisional.
 *
 *  - All three unitMinor values are 10 000 — the epic's $1 currency unit.
 *    Nothing on the wire moves (G2): $1.00 is wire 10000 in every currency
 *    and always was. What changed is that GOLD stopped being drawn on the
 *    legacy "1 unit = 1 cent" lens, which is what made the same wire integer
 *    render "$0.50" as a price and "⚡50.00" as an amount on one screen.
 */
const SEED: Readonly<Record<CurrencyCode, Readonly<Omit<CurrencyPresentation, "code">>>> = Object.freeze({
  RED: Object.freeze({
    displayName: "Red",
    symbol: "RED",
    color: "#ef4444",
    decimals: 2,
    unitMinor: 10_000,
    icon: Object.freeze({ kind: "image", src: coinRedUrl, alt: "Red coin" }) as CurrencyIconSpec,
  }),
  GREEN: Object.freeze({
    displayName: "Green",
    symbol: "GREEN",
    color: "#22c55e",
    decimals: 2,
    unitMinor: 10_000,
    icon: Object.freeze({ kind: "image", src: coinGreenUrl, alt: "Green coin" }) as CurrencyIconSpec,
  }),
  GOLD: Object.freeze({
    displayName: "Gold",
    symbol: "GOLD",
    color: "#f59e0b",
    decimals: 2,
    unitMinor: 10_000,
    icon: Object.freeze({ kind: "image", src: coinGoldUrl, alt: "Gold coin" }) as CurrencyIconSpec,
  }),
});

/** Monogram glyph: the first two characters of the resolved symbol, uppercased
 *  — short enough for a 1em badge and distinct across all three currencies. No
 *  currency seeds to it: it is reached by explicit configuration and by
 *  artwork that fails to load. */
export function monogramGlyph(symbol: string): string {
  return symbol.trim().slice(0, 2).toUpperCase();
}

const isNonBlank = (v: unknown): v is string => typeof v === "string" && v.trim() !== "";

/** A colour we are willing to hand to `style`. Hex, rgb()/rgba(), hsl()/hsla()
 *  and bare CSS keywords only — anything else (a url(), an expression, an empty
 *  string) falls back to the seed rather than being interpolated into style. */
function isSafeColor(v: unknown): v is string {
  if (!isNonBlank(v)) return false;
  const s = v.trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(s)) return true;
  if (/^(rgb|rgba|hsl|hsla)\([0-9a-z\s.,%/+-]*\)$/i.test(s)) return true;
  return /^[a-z]+$/i.test(s);
}

/** Reject anything that isn't a plain http(s), root-relative or data image URL —
 *  a `javascript:` src must never reach an <img>. */
function isSafeImageSrc(v: unknown): v is string {
  if (!isNonBlank(v)) return false;
  const s = v.trim();
  return /^https?:\/\//i.test(s) || s.startsWith("/") || /^data:image\//i.test(s);
}

function resolveDecimals(v: unknown, seed: number): number {
  if (typeof v !== "number" || !Number.isFinite(v)) return seed;
  const n = Math.trunc(v);
  if (n < 0) return 0;
  return n > MAX_DISPLAY_DECIMALS ? MAX_DISPLAY_DECIMALS : n;
}

/**
 * Validated the way `decimals` is: a junk configured value falls back to the
 * SEED, never to zero. A zero divisor renders every balance as Infinity, so
 * "fall back to something sane" is not a nicety here.
 */
function resolveUnitMinor(v: unknown, seed: number): number {
  if (typeof v !== "number" || !Number.isFinite(v)) return seed;
  const n = Math.trunc(v);
  return VALID_UNIT_MINORS.includes(n) ? n : seed;
}

function resolveIcon(v: unknown, seed: CurrencyIconSpec): CurrencyIconSpec {
  if (!v || typeof v !== "object") return seed;
  const { kind, src, alt } = v as Record<"kind" | "src" | "alt", unknown>;
  if (kind === "brand" || kind === "monogram") return { kind };
  if (kind === "image") {
    // A configured-but-unusable src is the commonest broken case, and it must
    // degrade to a drawable mark here rather than to an <img> that 404s.
    if (!isSafeImageSrc(src)) return seed;
    return isNonBlank(alt) ? { kind: "image", src: src.trim(), alt: alt.trim() } : { kind: "image", src: src.trim() };
  }
  return seed;
}

/**
 * Resolve one currency's presentation.
 *
 * Throws UnknownCurrencyError for a code outside the fixed set — see the note
 * on that class for why an unknown currency is never coerced to a known one.
 */
export function resolveCurrencyPresentation(
  code: string,
  settings?: CurrencyPresentationSettings,
): CurrencyPresentation {
  if (!isCurrencyCode(code)) throw new UnknownCurrencyError(String(code));
  const seed = SEED[code];
  const cfg = settings?.[code] ?? {};
  return {
    code,
    displayName: isNonBlank(cfg.displayName) ? cfg.displayName.trim() : seed.displayName,
    symbol: isNonBlank(cfg.symbol) ? cfg.symbol.trim() : seed.symbol,
    color: isSafeColor(cfg.color) ? cfg.color.trim() : seed.color,
    decimals: resolveDecimals(cfg.decimals, seed.decimals),
    unitMinor: resolveUnitMinor(cfg.unitMinor, seed.unitMinor),
    icon: resolveIcon(cfg.icon, seed.icon),
  };
}

/** Non-throwing variant for call sites holding an unvalidated wire code. */
export function tryResolveCurrencyPresentation(
  code: string,
  settings?: CurrencyPresentationSettings,
): CurrencyPresentation | null {
  return isCurrencyCode(code) ? resolveCurrencyPresentation(code, settings) : null;
}

/** Every currency, resolved — for pickers, legends and settings previews. */
export function resolveAllCurrencyPresentations(
  settings?: CurrencyPresentationSettings,
): CurrencyPresentation[] {
  return CURRENCY_CODES.map((code) => resolveCurrencyPresentation(code, settings));
}
